export const MONTHS = [
  { value: 1, label: 'Enero', short: 'Ene' },
  { value: 2, label: 'Febrero', short: 'Feb' },
  { value: 3, label: 'Marzo', short: 'Mar' },
  { value: 4, label: 'Abril', short: 'Abr' },
  { value: 5, label: 'Mayo', short: 'May' },
  { value: 6, label: 'Junio', short: 'Jun' },
  { value: 7, label: 'Julio', short: 'Jul' },
  { value: 8, label: 'Agosto', short: 'Ago' },
  { value: 9, label: 'Septiembre', short: 'Sep' },
  { value: 10, label: 'Octubre', short: 'Oct' },
  { value: 11, label: 'Noviembre', short: 'Nov' },
  { value: 12, label: 'Diciembre', short: 'Dic' }
];

const pad2 = (n) => String(n).padStart(2, '0');

export const formatCurrencyAmount = (value, currency) => Number(value || 0).toLocaleString('es-AR', {
  minimumFractionDigits: currency === '$' ? 0 : 2,
  maximumFractionDigits: currency === '$' ? 0 : 2
});

export const getMonthRange = (year, month) => {
  const lastDay = new Date(Number(year), Number(month), 0).getDate();
  const from = `${year}-${pad2(month)}-01`;
  const to = `${year}-${pad2(month)}-${pad2(lastDay)}`;
  return { from, to };
};

export const hasNumeric = (value) => {
  if (value === null || value === undefined || value === '') return false;
  const n = Number(value);
  return !Number.isNaN(n);
};

export const toUsd = (value, currency, conversion) => {
  const amount = Number(value);
  if (Number.isNaN(amount)) return null;
  if ((currency || '$') === 'USD') return amount;

  const conv = Number(conversion);
  if (Number.isNaN(conv) || conv <= 0) return null;
  return amount / conv;
};

export const getDisplayMoney = (value, currency, conversion, convertPesosToUsd) => {
  const baseCurrency = currency || '$';
  const amount = Number(value);
  if (Number.isNaN(amount)) return null;

  if (!convertPesosToUsd || baseCurrency !== '$') {
    return { amount, currency: baseCurrency };
  }

  const usd = toUsd(amount, baseCurrency, conversion);
  if (usd === null) {
    return { amount, currency: baseCurrency };
  }

  return { amount: usd, currency: 'USD' };
};

const addCurrencyTotal = (map, currency, amount) => {
  map[currency] = (map[currency] || 0) + amount;
};

export const getCurrencyOrder = (currency) => {
  if (currency === '$') return 0;
  if (currency === 'USD') return 1;
  return 2;
};

const sortByCurrency = (a, b) => {
  const order = getCurrencyOrder(a.currency) - getCurrencyOrder(b.currency);
  if (order !== 0) return order;
  return String(a.currency).localeCompare(String(b.currency), undefined, { sensitivity: 'base' });
};

export const filterExpensesByRange = (expenses, from, to) => (expenses || []).filter((e) => {
  const d = e.date?.slice(0, 10) || '';
  if (!d) return false;
  return d >= from && d <= to;
});

export const filterServicesByRange = (services, from, to) => (services || []).filter((s) => {
  const d = (s.date_profitability ?? s.dateProfitability)?.slice(0, 10) || '';
  if (!d) return false;
  return d >= from && d <= to;
});

export const computeProfitabilityTotals = (filteredExpenses, filteredServices, convertPesosToUsd) => {
  const expensesMap = {};
  const servicesMap = {};

  for (const e of (filteredExpenses || [])) {
    const display = getDisplayMoney(e.value, e.currency, e.conversion, convertPesosToUsd);
    if (!display) continue;
    addCurrencyTotal(expensesMap, display.currency, display.amount);
  }

  for (const s of (filteredServices || [])) {
    if (!hasNumeric(s.price) || !hasNumeric(s.cost)) continue;
    const bankExpense = hasNumeric(s.bank_expense ?? s.bankExpense) ? Number(s.bank_expense ?? s.bankExpense) : 0;
    const profit = Number(s.price) - Number(s.cost) - bankExpense;
    if (Number.isNaN(profit)) continue;
    const display = getDisplayMoney(profit, s.currency, s.conversion, convertPesosToUsd);
    if (!display) continue;
    addCurrencyTotal(servicesMap, display.currency, display.amount);
  }

  const sortRows = (map) => Object.entries(map)
    .map(([currency, total]) => ({ currency, total }))
    .sort(sortByCurrency);

  const allCurrencies = new Set([...Object.keys(servicesMap), ...Object.keys(expensesMap)]);
  const netRows = [...allCurrencies].map((currency) => ({
    currency,
    total: (servicesMap[currency] || 0) - (expensesMap[currency] || 0)
  })).sort(sortByCurrency);

  return {
    expenses: sortRows(expensesMap),
    profitability: sortRows(servicesMap),
    net: netRows
  };
};
